/** @jsxImportSource @emotion/react */
import ProgressBar from '@ProgressBar';
import { useSelector } from 'react-redux';
import useIsForwardNavigationBlockedBySupplierCard from '../../Hooks/useIsForwardNavigationBlockedBySupplierCard/useIsForwardNavigationBlockedBySupplierCard';
import useIsForwardNavigationBlockedByNavigationControl from '../../Hooks/useIsForwardNavigationBlockedByNavigationControl/useIsForwardNavigationBlockedByNavigationControl';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import { calculateProgressValue } from './Progress.helpers';
import useStagedProgressBar from '../../Hooks/useStagedProgressBar/useStagedProgressBar';
import StagedProgressBarContainer from '../StagedProgressBar/StagedProgressBarContainer';

const Progress = () => {
  const { currentPage, totalPages } = useSelector((store) => store.navigation);
  const components = useSelector((store) => store.components);
  const {
    headerEnabled,
    headlineText,
    headlineLogo,
    submissionPageNumber,
    enableStagedProgressBar,
    isProgressBarHidden,
  } = useSelector((store) => store.formConfig);
  const { progressBar } = useSelector(
    (store) => store.formConfig?.fixedComponents
  );
  const { isNextPageCpcCard, showCpcRecommendations } = useSelector(
    (store) => store.cpcCard
  );

  const progressBarExperimentName = useGetExperimentName({
    componentName: 'ProgressBar',
  });

  const showingCpcCard = !!(isNextPageCpcCard || showCpcRecommendations);
  const { isForwardNavigationBlockedBySupplierCard } =
    useIsForwardNavigationBlockedBySupplierCard();
  const { isForwardNavigationBlockedByNavigationControl } =
    useIsForwardNavigationBlockedByNavigationControl();

  // Some form journeys will result in an alternative endpoint, such as a CPC card, supplier card or a navigation control blocking forward navigation. In these cases, we want to show the progress bar as complete.
  const isAlternativeFormEnding =
    showingCpcCard ||
    isForwardNavigationBlockedBySupplierCard ||
    isForwardNavigationBlockedByNavigationControl;

  const progressValue =
    isAlternativeFormEnding === true
      ? 100
      : calculateProgressValue(currentPage, totalPages, submissionPageNumber);

  const headerIsPresent = headerEnabled && (headlineLogo || headlineText);

  // Use the custom hook to manage the staged progress bar state
  const { stages } = useStagedProgressBar({
    enableStagedProgressBar,
    currentPage,
    submissionPageNumber,
    components,
  });

  // If progress bar is hidden, return invisible div that preserves space allocation
  if (isProgressBarHidden) {
    return (
      <div
        style={{ height: 25, width: '100%', visibility: 'hidden' }}
        data-testid="hiddenProgressBarDiv"
      />
    );
  }

  if (totalPages === 1 && !headerIsPresent) {
    return <div style={{ height: 25, width: '100%' }} />;
  }

  if (totalPages === 1 && headerIsPresent) {
    return null;
  }

  if (
    typeof submissionPageNumber === 'number' &&
    currentPage > submissionPageNumber
  ) {
    // Don't show the progress bar if we have passed the submission page
    if (!headerIsPresent) {
      return <div style={{ height: 25, width: '100%' }} />;
    }

    return null;
  }

  if (enableStagedProgressBar && totalPages > 1 && stages.length > 0) {
    return (
      <StagedProgressBarContainer stages={stages} currentPage={currentPage} />
    );
  }

  return (
    <ProgressBar
      progress={progressValue}
      text={progressBar?.text ?? 'Progress:'}
      experimentName={progressBarExperimentName}
      dataTestId="progressBar"
      totalSteps={
        typeof submissionPageNumber === 'number'
          ? submissionPageNumber + 1
          : totalPages
      }
    />
  );
};

export default Progress;
